<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline'
  import { EnvelopeIcon, ArrowPathIcon } from '@heroicons/vue/24/solid'

  const props = defineProps<{
    show: boolean
    status: 'loading' | 'success' | 'error'
    integrationName: string
  }>()

  const emit = defineEmits(['complete', 'retry'])

  const countdown = ref(5)
  const timer = ref<number | null>(null)

  watch(
    () => props.show && props.status === 'success',
    (shouldStartCountdown) => {
      if (shouldStartCountdown) {
        startCountdown()
      }
    }
  )

  const startCountdown = () => {
    countdown.value = 5
    timer.value = window.setInterval(() => {
      countdown.value--
      if (countdown.value <= 0) {
        clearInterval(timer.value!)
        emit('complete')
      }
    }, 1000)
  }

  const handleRetry = () => {
    emit('retry')
  }

  const handleEmailSupport = () => {
    window.location.href = 'mailto:support@jupus.de'
  }
</script>

<template>
  <Teleport to="body">
    <transition
      enter-active-class="ease-out duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="ease-in duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-50 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div class="flex min-h-screen items-center justify-center p-4">
          <div class="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />

          <div
            class="relative w-full max-w-lg transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all"
            :class="{ 'p-8': status !== 'loading', 'p-6': status === 'loading' }"
          >
            <!-- Loading State -->
            <div
              v-if="status === 'loading'"
              class="flex flex-col items-center justify-center py-4"
            >
              <div class="loading-spinner">
                <div
                  v-for="n in 4"
                  :key="n"
                  class="spinner-quad"
                />
              </div>
              <p class="mt-6 text-base font-medium text-gray-900">Anfrage wird exportiert...</p>
            </div>

            <!-- Success State -->
            <div
              v-else-if="status === 'success'"
              class="flex flex-col items-center space-y-4"
            >
              <div class="flex h-16 w-16 items-center justify-center rounded-full bg-purple-50">
                <CheckCircleIcon class="h-10 w-10 text-purple-600" />
              </div>

              <div class="text-center">
                <h3 class="text-xl font-semibold text-gray-900">Export erfolgreich</h3>
                <div class="mt-2 space-y-2">
                  <p class="text-sm text-gray-600">
                    Ihre Akte wurde erfolgreich an {{ integrationName }} exportiert.
                  </p>
                  <p class="text-sm font-medium text-purple-600">
                    Weiterleitung in {{ countdown }} Sekunden...
                  </p>
                </div>
              </div>

              <div class="mt-2 h-1 w-full overflow-hidden rounded-full bg-purple-100">
                <div
                  class="h-full bg-purple-600 transition-all duration-1000"
                  :style="{ width: `${(countdown / 5) * 100}%` }"
                />
              </div>
            </div>

            <!-- Error State -->
            <div
              v-else
              class="flex flex-col items-center space-y-4"
            >
              <div class="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
                <XCircleIcon class="h-10 w-10 text-red-600" />
              </div>

              <div class="text-center">
                <h3 class="text-xl font-semibold text-gray-900">Export fehlgeschlagen</h3>
                <p class="mt-2 text-sm text-gray-600">
                  Beim Export Ihrer Akte ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut
                  oder kontaktieren Sie unseren Support.
                </p>
              </div>

              <div class="flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
                <button
                  type="button"
                  class="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm
                    font-semibold text-white shadow-sm hover:bg-purple-500 focus:outline-none focus:ring-2
                    focus:ring-purple-600 focus:ring-offset-2"
                  @click="handleRetry"
                >
                  <ArrowPathIcon class="h-4 w-4" />
                  Erneut versuchen
                </button>

                <button
                  type="button"
                  class="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5
                    text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2
                    focus:ring-purple-600 focus:ring-offset-2"
                  @click="handleEmailSupport"
                >
                  <EnvelopeIcon class="h-4 w-4" />
                  Support kontaktieren
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
  .loading-spinner {
    position: relative;
    width: 80px;
    height: 80px;
    animation: spin 1.5s linear infinite;
  }

  .spinner-quad {
    position: absolute;
    width: 40%;
    height: 40%;
    border-radius: 50%;
    animation: pulse 1.5s ease-in-out infinite;
  }

  .spinner-quad:nth-child(1) {
    top: 0;
    left: 0;
    background-color: #9333ea;
  }

  .spinner-quad:nth-child(2) {
    top: 0;
    right: 0;
    background-color: #a855f7;
    animation-delay: 0.4s;
  }

  .spinner-quad:nth-child(3) {
    bottom: 0;
    right: 0;
    background-color: #c084fc;
    animation-delay: 0.8s;
  }

  .spinner-quad:nth-child(4) {
    bottom: 0;
    left: 0;
    background-color: #e9d5ff;
    animation-delay: 1.2s;
  }

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0%,
    100% {
      transform: scale(0.5);
      opacity: 0.5;
    }
    50% {
      transform: scale(1);
      opacity: 1;
    }
  }
</style>
